import { v4 as uuidv4 } from "uuid";
import Vue from "vue";

import { ProjectsModule } from "@/store/modules/ProjectsModule";
import { PanelModule } from "@/store/modules/PanelModule";
import { ClientPanelModule } from "@/store/modules/ClientPanelModule";
import { ProgramChainModule } from "@/store/modules/ProgramChainModule";
import { ProjectDetailsModule } from "@/store/modules/ProjectDetailsModule";
import { UserModule } from "@/store/modules/UserModule";
import { ComBoxModule } from "@/store/modules/ComBoxesModule";
import { SchedulingModule } from "@/store/modules/SchedulingModule";
import router from '@/router/router'
import { ToolbarContentModule } from '@/store/modules/ToolbarContent'
import { db } from "@/main";
import { i18n } from "@/plugins/vuetify";
import { firestore } from "firebase";
import { getActiveUserId } from "@/data/repository/UserRepository";
import { RecordInfo } from "@/data/models/RecordInfo";
import { 
  ProjectLastOperation, 
  ProjectOperationName, 
  ProjectOperationReason, 
  ProjectOperationSource 
} from "@/data/models/ProjectDetails";
import { COLOR_LAST_VISITED_ITEM } from "./colors";
import { 
  getString, 
  setString, 
  STORAGE_KEY_LAST_VISITED_ITEM_LEVEL_1,
  STORAGE_KEY_LAST_VISITED_ITEM_LEVEL_2,
  STORAGE_KEY_LAST_VISITED_ITEM_LEVEL_3,
  STORAGE_KEY_LAST_VISITED_ITEM_LEVEL_4,
  STORAGE_KEY_LAST_VISITED_ITEM_LEVEL_5,
} from "./SecureLocalStorage";
import { DevicesModule } from "@/store/modules/DevicesModule";
import { DeviceCategoriesAndTemplatesModule } from "@/store/modules/DeviceCategoriesAndTemplatesModule";
import { DeviceTemplate } from "@/data/models/DeviceTemplate";

export const CSV_SEPARATOR = ";";
export const DELETION_SEPARATOR = "!";

export enum StorageKeyLastVisitedItemLevel {
  LEVEL_1 = 1,
  LEVEL_2 = 2,
  LEVEL_3 = 3,
  LEVEL_4 = 4,
  LEVEL_5 = 5,
}

export enum DetailsSeparator {
  PARENTHESES = "PARENTHESES",
  COLON = "COLON",
}

export function notEmpty<TValue>(
  value: TValue | null | undefined
): value is TValue {
  return value !== null && value !== undefined;
}

export function clearUserModule() {
  UserModule.clear();
}

export function clearCacheAndModules(alsoClearUserModule: boolean, shouldClearDevicesModule?: boolean) {
  if (alsoClearUserModule) {
    clearUserModule();
  }

  // "View as user" must never survive logout/login or client switch.
  // Keep this reset in the shared auth cleanup path to avoid stale cross-session state.
  /* eslint-disable @typescript-eslint/no-var-requires */
  const { clearTargetUserSelectionForNewSession } = require("@/store/modules/TargetUserModule");
  /* eslint-enable @typescript-eslint/no-var-requires */
  clearTargetUserSelectionForNewSession();
  
  ProjectsModule.clear();
  PanelModule.clear();
  ComBoxModule.clear();
  ClientPanelModule.clear();
  ProgramChainModule.clear();

  // A device's assignment history is exactly what changes when it moves between
  // clients, so the session cache behind it must not survive a client switch.
  /* eslint-disable @typescript-eslint/no-var-requires */
  const {
    clearCachedIresDeviceAssignments,
  } = require("@/data/repository/IresDeviceAssignmentRepository");
  /* eslint-enable @typescript-eslint/no-var-requires */
  clearCachedIresDeviceAssignments();

  // The IRES Admin Client's read cache holds one user's answers; required lazily like the above.
  /* eslint-disable @typescript-eslint/no-var-requires */
  const { clearAdminReadCache } = require("@/utils/AdminReadCache");
  /* eslint-enable @typescript-eslint/no-var-requires */
  clearAdminReadCache();

  SchedulingModule.clear();
  DeviceCategoriesAndTemplatesModule.clear();

  if (shouldClearDevicesModule) {
    DevicesModule.clear();
  }

  ProjectDetailsModule.clearProjectDetailsState();

  // Lazy-require settings modules to avoid circular dependency at module load time.
  // Utils.ts is imported early by model files (e.g. ComBoxClientConfig.ts), and top-level
  // imports of these settings modules would create a circular chain that leaves Report.ts
  // exports unresolved when Client.ts initialises.
  /* eslint-disable @typescript-eslint/no-var-requires */
  const { HyDryDeviceSettingsModule } = require("@/store/modules/HyDryDeviceSettingsModule");
  const { HyDryMessagesSettingsModule } = require("@/store/modules/HyDryMessagesSettingsModule");
  const { ReportSettingsModule } = require("@/store/modules/ReportSettingsModule");
  const { CompanySettingsModule } = require("@/store/modules/CompanySettingsModule");
  const { ProjectManagementSettingsModule } = require("@/store/modules/ProjectManagementSettingsModule");
  const { AllDevicesSettingsModule } = require("@/store/modules/AllDevicesSettingsModule");
  const { UserSettingsModule } = require("@/store/modules/UserSettingsModule");
  const { SystemConfigurationModule } = require("@/store/modules/SystemConfigurationModule");
  const { RoleSettingsModule } = require("@/store/modules/RoleSettingsModule");
  const { FinishedProjectsSettingsModule } = require("@/store/modules/FinishedProjectsSettingsModule");
  const { BranchesModule } = require("@/store/modules/BranchesModule");
  /* eslint-enable @typescript-eslint/no-var-requires */

  HyDryDeviceSettingsModule.clear();
  HyDryMessagesSettingsModule.clear();
  ReportSettingsModule.clear();
  CompanySettingsModule.clear();
  ProjectManagementSettingsModule.clear();
  AllDevicesSettingsModule.clear();
  UserSettingsModule.clear();
  SystemConfigurationModule.clear();
  RoleSettingsModule.clear();
  FinishedProjectsSettingsModule.clear();
  BranchesModule.clear();
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function nullIfUndefined(value: any) {
  return value == undefined ? null : value;
}

export function getFileNameWithoutExtension(fileName: string): string {
  return fileName.split(".")[0]
}

export function isUuid(value: string) {
  return value.match(
    "/^[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i"
  );
}

export function generateRandomUuid() {
  return uuidv4();
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function filterOutUndefined(pojo: any): any {
  const target = {
    ...pojo,
  };

  Object.keys(target).forEach((key) =>
    target[key] === undefined ? delete target[key] : ""
  );

  return target;
}

export async function showConfirmDialog(
  vue: Vue,
  title: string,
  msg: string
): Promise<boolean | undefined> {
  return vue.$confirm(msg, {
    title: title,
    buttonTrueText: vue.$t("common.yes").toString(),
    buttonFalseText: vue.$t("common.abort").toString(),
  });
}

export function clamp(value: number | null, min: number, max: number) {
  if (value == null) {
    return null;
  }
  return Math.max(min, Math.min(max, value));
}

export function roundWithPrecision(value: number, precision: number) {
  const factor = Math.pow(10, precision);

  return Math.round(value * factor + Number.EPSILON) / factor;
}

export function redirectToCockpit(scope: Vue) {
  router.replace({
    path: `/main/cockpit`,
  }).then(() => {
    scope.$nextTick(() => {
      ToolbarContentModule.setToolbarTitle(scope.$t('pages.cockpit').toString());
    });
  });
}

/**
 * Checks if a given string is not empty.
 *
 * @param str The string value to be checked for emptiness.
 * @returns Returns `true` if the string is not empty, else `false`.
 */
export function isNotEmpty(str: string | undefined | null) {
  return str ? str.trim().length > 0 : false;
}

/**
 * Checks if a given strings are not equal.
 *
 * @param a The first string value to be checked.
 * @param b The second string value to be checked.
 * @returns Returns `true` if the strings are not equal, else `false`.
 */
export function areTrimmedStringsDifferent(a: string, b: string) {
  return a.trim() !== b.trim();
}

/**
 * Checks whether two strings are the same value, ignoring surrounding whitespace and case.
 *
 * Use this when comparing against a fixed IRES constant (manufacturer, electricity meter
 * manufacturer) rather than when detecting that a user changed a value: an export writes
 * "IRES", but a CSV that has been opened and re-saved in Excel often comes back as "Ires",
 * and that must not reject the row. Real change detection stays case-sensitive, because
 * correcting the spelling of a manufacturer is a change worth writing.
 *
 * @param a The first string value to be checked.
 * @param b The second string value to be checked.
 * @returns Returns `true` if the strings are equal ignoring whitespace and case, else `false`.
 */
export function areTrimmedStringsEqualIgnoringCase(a: string, b: string) {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * Splits an array into batches of a specified size.
 *
 * @param array The array to be split into batches.
 * @param batchSize The size of each batch.
 * @returns An array containing batches of elements from the original array.
 */
export function splitIntoBatches <T>(array: T[], batchSize: number) {
  const batches = [];
  for (let i = 0; i < array.length; i += batchSize) {
    const batch = array.slice(i, i + batchSize);
    batches.push(batch);
  }
  return batches;
}

function formatNumberWithLeadingZeros(num: number, targetLength = 3): string {
  return String(num).padStart(targetLength, "0");
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function findHighestDeletionSuffix(objects: any[], key: string): number {
  let maxSuffix = 0;

  objects.forEach((obj) => {
    if (obj[key]) {
      const parts = obj[key].split(DELETION_SEPARATOR);
      const suffix = parseInt(parts[1], 10);

      if (suffix > maxSuffix) {
        maxSuffix = suffix;
      }
    }
  });

  return maxSuffix;
}

/**
 * Retrieves and updates IDs after deletion.
 * 
 * This function takes an array of IDs, retrieves corresponding documents 
 * from a Firestore collection, and finds suitable deletion suffixes for each ID.
 * It utilizes Firestore transactions for data consistency and efficiency.
 * 
 * @param ids An array of string IDs to be processed.
 * @param key The key used for querying documents.
 * @param collectionRef The reference to the Firestore collection.
 * @returns A Promise resolving to an array of maps where each map contains original ID as key and updated ID with deletion suffix as value.
 */
 export async function getUpdatedIdsAfterDeletion(ids: string[], key: string, collectionRef: string): Promise<Map<string, string>> {
  const minDeletionSuffix = "001";
  const maxDeletionSuffix = "999";
  const updatedIdsMap = new Map<string, string>();
  let emptyStringCount = 0;

  try {
    const batchedIds = ids.map(id => ({
      id,
      minQuery: id + DELETION_SEPARATOR + minDeletionSuffix,
      maxQuery: id + DELETION_SEPARATOR + maxDeletionSuffix
    }));

    // Start a Firestore transaction
    await db.runTransaction(async (transaction) => {
      const promises = batchedIds.map(async ({ id, minQuery, maxQuery }) => {
        if (id === "") {
          // If the ID is an empty string, assign deletion suffix accordingly
          const updatedId = ++emptyStringCount;
          updatedIdsMap.set((updatedId).toString(), DELETION_SEPARATOR + formatNumberWithLeadingZeros(updatedId));
        }
        else {
          const docRef = db.collection(collectionRef).doc(id);
          const doc = await transaction.get(docRef);

          if (!doc.exists) {
            // If document doesn't exist, use minimum deletion suffix
            updatedIdsMap.set(id, id + DELETION_SEPARATOR + minDeletionSuffix);
          } else {
            // Query documents within deletion suffix range
            const querySnapshot = await db.collection(collectionRef)
              .where(key, ">=", minQuery)
              .where(key, "<=", maxQuery)
              .get();

            const objects = querySnapshot.docs.map(doc => doc.data());

            if (objects.length === 0) {
              // If no matching documents found, use minimum deletion suffix
              updatedIdsMap.set(id, id + DELETION_SEPARATOR + minDeletionSuffix);
            } else {
              // Find highest deletion suffix and generate next available suffix
              const currentDeletionSuffix = findHighestDeletionSuffix(objects, key);
              updatedIdsMap.set(id, id + DELETION_SEPARATOR + formatNumberWithLeadingZeros(currentDeletionSuffix + 1));
            }
          }
        }
      });

      await Promise.all(promises);
    });

    return updatedIdsMap;
  } catch (error) {
    return updatedIdsMap;
  }
}

export async function getUpdatedIdAfterDeletion(id: string, key: string, collectionRef: string): Promise<string> {
  const minDeletionSuffix = "!001"
  const maxDeletionSuffix = "!999"
  
  try {
    const querySnapshot = await db
      .collection(collectionRef)
      .where(key, ">=", id+minDeletionSuffix)
      .where(key, "<=", id+maxDeletionSuffix)
      .get();

    const objects = querySnapshot.docs.map((doc) => doc.data());

    if (objects.length === 0) {
      return id+minDeletionSuffix;
    }
    else {
      const currentDeletionSuffix = findHighestDeletionSuffix(objects, key);
      return id+DELETION_SEPARATOR+formatNumberWithLeadingZeros(currentDeletionSuffix+1);
    }
  }
  catch (error) {
    return "";
  }
}

/**
 * Releases the QR code of a device that is being deleted, the way its inventory number is released:
 * the code gets the next free deletion suffix ("FU-0010-0042" becomes "FU-0010-0042!001"). The QR
 * checks count deleted devices too, so a code left in place blocked the label from ever going on
 * another device (decided 2026-10-29), while the suffixed code still shows what the device carried.
 *
 * @param qrCode - the code the deleted device carries.
 * @param collectionRef - the collection the device lives in (generic devices or client panels).
 * @returns `undefined` when there is nothing to release (no code, or already released), the released
 *   code, or `""` when the next suffix could not be read, which the caller treats as a failed deletion.
 */
export async function getReleasedQrCode(qrCode: string | null | undefined, collectionRef: string): Promise<string | undefined> {
  if (!qrCode || !isNotEmpty(qrCode) || qrCode.includes(DELETION_SEPARATOR)) {
    return undefined;
  }

  return getUpdatedIdAfterDeletion(qrCode, "qrCode", collectionRef);
}

/**
 * {@link getReleasedQrCode} for several devices deleted together.
 *
 * @param qrCodesById - each device's id (serial number for a client panel) and the code it carries.
 * @param collectionRef - the collection the devices live in.
 * @returns the released code per device id, only for devices that had one to release, or `null`
 *   when any of them could not be released, so the caller can refuse the whole deletion.
 */
export async function getReleasedQrCodes(
  qrCodesById: Map<string, string | null | undefined>,
  collectionRef: string
): Promise<Map<string, string> | null> {
  const entries = Array.from(qrCodesById.entries());
  const releasedCodes = await Promise.all(entries.map(([, qrCode]) => getReleasedQrCode(qrCode, collectionRef)));

  if (releasedCodes.some((releasedCode) => releasedCode === "")) {
    return null;
  }

  const releasedById = new Map<string, string>();
  entries.forEach(([id], index) => {
    const releasedCode = releasedCodes[index];
    if (releasedCode) {
      releasedById.set(id, releasedCode);
    }
  });

  return releasedById;
}

// TO BE USED LATER
export async function getUpdatedIdAfterRestoration(id: string, key: string, collectionRef: string): Promise<string> {
  const idWithoutDeletionSuffix = id.split(DELETION_SEPARATOR)[0];
  
  try {
    const querySnapshot = await db
      .collection(collectionRef)
      .where(key, "==", idWithoutDeletionSuffix)
      .limit(1)
      .get();

    const objects = querySnapshot.docs.map((doc) => doc.data());

    if (objects.length !== 0) {
      return "!";
    }
    else {
      return idWithoutDeletionSuffix;
    }
  }
  catch (error) {
    return "!!";
  }
}

export function isValidInput(input: string | null) {
  if (!input) {
    return true;
  }

  return !input.includes(CSV_SEPARATOR) && !input.includes(DELETION_SEPARATOR);
}

/**
 * Returns Vuetify validation rules for optional email fields.
 * Allows empty values but rejects invalid email formats.
 */
export function getEmailRules(errorMessage: string) {
  return [
    (v: string) => !v || /.+@.+\..+/.test(v) || errorMessage,
  ];
}

export function formatNumber(value: number, useGrouping = true) {
  const formatter = new Intl.NumberFormat(i18n.locale, {
    useGrouping: useGrouping
  });

  return formatter.format(value);
}

/**
 * Deeply compares two objects for equality. It checks if every property of both objects have the same values,
 * including nested objects. The comparison is performed recursively to handle deep object structures.
 *
 * @param {T} obj1 - The first object to compare.
 * @param {T} obj2 - The second object to compare.
 * @returns {boolean} - Returns `true` if both objects are equal, including their nested structures. Returns `false` otherwise.
 * @template T - The type of the objects to compare. This ensures type safety when comparing properties.
 *
 * @example
 * const obj1 = { a: 1, b: { c: 2 } };
 * const obj2 = { a: 1, b: { c: 2 } };
 * console.log(deepEqual(obj1, obj2)); // Output: true
 */
function deepEqual<T>(obj1: T, obj2: T): boolean {
  if (obj1 === obj2) return true;
  if (
    typeof obj1 !== "object" ||
    typeof obj2 !== "object" ||
    obj1 == null ||
    obj2 == null
  )
    return false;

  const keys1 = Object.keys(obj1) as (keyof T)[];
  const keys2 = Object.keys(obj2) as (keyof T)[];

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) return false;
  }

  return true;
}

/**
 * Compares two sets for equality. Two sets are considered equal if they contain the same elements.
 * This function does not consider the order of elements since sets are unordered collections.
 *
 * @param {Set<T>} set1 - The first set to compare.
 * @param {Set<T>} set2 - The second set to compare.
 * @returns {boolean} - Returns `true` if both sets contain the same elements. Returns `false` otherwise.
 * @template T - The type of the elements in the sets. This ensures type safety when comparing elements.
 *
 * @example
 * const set1 = new Set([1, 2, 3]);
 * const set2 = new Set([3, 2, 1]);
 * console.log(compareSets(set1, set2)); // Output: true
 */
export function compareSets<T>(set1: Set<T>, set2: Set<T>): boolean {
  if (set1.size !== set2.size) return false;
  for (const item of set1) {
    if (!set2.has(item)) return false;
  }
  return true;
}

/**
 * Compares two arrays for deep equality. Two arrays are considered equal if they have the same length
 * and each element in the same position in both arrays is equal, including nested objects or arrays.
 *
 * @param {T[]} arr1 - The first array to compare.
 * @param {T[]} arr2 - The second array to compare.
 * @returns {boolean} - Returns `true` if both arrays are equal, including their nested structures. Returns `false` otherwise.
 * @template T - The type of the elements in the arrays. This ensures type safety and deep equality checks on nested structures.
 *
 * @example
 * const arr1 = [1, { a: 2 }, [3]];
 * const arr2 = [1, { a: 2 }, [3]];
 * console.log(compareArrays(arr1, arr2)); // Output: true
 */
export function compareArrays<T>(arr1: T[], arr2: T[]): boolean {
  if (arr1.length !== arr2.length) return false;
  for (let i = 0; i < arr1.length; i++) {
    if (!deepEqual(arr1[i], arr2[i])) return false;
  }
  return true;
}

/**
 * Compares two arrays for equality, disregarding the order of elements.
 * This function first checks if both arrays are of the same length. Then, it sorts both arrays
 * and compares each element in sequence. This method ensures that the arrays are treated as equal
 * if they contain the same elements in any order.
 *
 * @param {Array} arr1 - The first array to be compared.
 * @param {Array} arr2 - The second array to be compared.
 * @returns {boolean} - Returns true if both arrays contain the same elements regardless of the order, false otherwise.
 *
 * @example
 * // Returns true
 * console.log(compareArraysIgnoreOrder([1, 2, 3], [3, 1, 2]));
 *
 * @example
 * // Returns false
 * console.log(compareArraysIgnoreOrder([1, 2, 3], [1, 2, 4]));
 */
 export function compareArraysIgnoreOrder<T>(arr1: T[], arr2: T[]): boolean {
  if (!arr1 || !arr2) {
    return true;
  }

  if (arr1.length !== arr2.length) {
    return false;
  }

  // Sort both arrays and compare each element
  const sortedArr1 = arr1.slice().sort();
  const sortedArr2 = arr2.slice().sort();

  for (let i = 0; i < sortedArr1.length; i++) {
    if (sortedArr1[i] !== sortedArr2[i]) {
      return false;
    }
  }

  return true;
}

/**
 * Filters an array of objects by ensuring that each value of a specified field
 * appears only once in the resulting array. The function assumes the values of
 * the field are strings.
 * 
 * @template T - The object type of the array.
 * @template K - The key of the object that will be used for filtering.
 * 
 * @param {T[]} arr - The array of objects to be filtered.
 * @param {K} field - The key in object T used to determine uniqueness.
 * 
 * @returns {T[]} A filtered array containing only the first occurrence of each
 * unique value for the specified field.
 *
 * @example
 * // Define a type where 'a' and 'b' are strings
 * type MyObject = { a: string; b: string };
 *
 * // Create an array of MyObject
 * const data: MyObject[] = [
 *     { a: "a", b: "b" },
 *     { a: "a", b: "c" },
 *     { a: "d", b: "e" }
 * ];
 *
 * // Filter the array to include only unique 'a' values
 * const filteredData = filterUniqueByField(data, 'a');
 * console.log(filteredData); // Outputs: [{ a: "a", b: "b" }, { a: "d", b: "e" }]
 */
export function filterUniqueByField<
  T extends Record<K, string>,
  K extends keyof T
>(arr: T[], field: K): T[] {
  const seen = new Set<string>();
  return arr.filter((item) => {
    const fieldValue = item[field];
    if (!seen.has(fieldValue)) {
      seen.add(fieldValue);
      return true;
    }
    return false;
  });
}

export function getLastTimeChanged() {
  return {
    recordedAt: firestore.Timestamp.now(),
    recordedByUserId: getActiveUserId(),
  } as RecordInfo;
}

export function getProjectLastOperation(
  operationName: ProjectOperationName,
  timestamp?: firestore.Timestamp
) {
  return {
    name: operationName,
    timestamp: timestamp ?? firestore.Timestamp.now(),
    recordedByUserId: getActiveUserId(),
    reason: ProjectOperationReason.USER_ACTION,
    source: ProjectOperationSource.PORTAL
  } as ProjectLastOperation;
}

export function isNullOrBlank(val?: string | null) {
  if (!val || val.trim() === "") {
    return true;
  }

  return false;
}

export function nullIfBlank(val?: string) {
  if (isNullOrBlank(val)) {
    return null;
  }

  return val ?? null;
}

export function applyDetailsSeparator(
  text: string,
  detailsSeparator: DetailsSeparator
) {
  switch (detailsSeparator) {
    case DetailsSeparator.COLON:
      return `: ${text}`;

    default:
      return ` (${text})`;
  }
}

/**
 * Returns the storage key associated with the last visited item at the specified level.
 * 
 * @param {StorageKeyLastVisitedItemLevel} level - The level of the last visited item.
 * @returns {string} The storage key for the specified level.
 */
function getStorageKeyLastVisitedItem(level: StorageKeyLastVisitedItemLevel) {
  switch (level) {
    case StorageKeyLastVisitedItemLevel.LEVEL_2:
      return STORAGE_KEY_LAST_VISITED_ITEM_LEVEL_2;
    case StorageKeyLastVisitedItemLevel.LEVEL_3:
      return STORAGE_KEY_LAST_VISITED_ITEM_LEVEL_3;
    case StorageKeyLastVisitedItemLevel.LEVEL_4:
      return STORAGE_KEY_LAST_VISITED_ITEM_LEVEL_4;
    case StorageKeyLastVisitedItemLevel.LEVEL_5:
      return STORAGE_KEY_LAST_VISITED_ITEM_LEVEL_5;

    default:
      return STORAGE_KEY_LAST_VISITED_ITEM_LEVEL_1;
  }
}

/**
 * Clears the last visited items for the specified levels.
 * 
 * @param {...StorageKeyLastVisitedItemLevel} levels - The levels to clear the last visited items for.
 */
function clearLastVisitedItems(
  ...levels: StorageKeyLastVisitedItemLevel[]
) {
  levels.forEach((level) => setString(getStorageKeyLastVisitedItem(level)));

  return;
}

/**
 * Gets the levels after the current level.
 * 
 * @param {StorageKeyLastVisitedItemLevel} level - The current level.
 * @returns {StorageKeyLastVisitedItemLevel[]} An array of levels after the current level.
 */
function getLevelsAfterCurrent(level: StorageKeyLastVisitedItemLevel): StorageKeyLastVisitedItemLevel[] {
  return Object.values(StorageKeyLastVisitedItemLevel)
    .filter((lvl) => typeof lvl === 'number' && (lvl as number) > level) as StorageKeyLastVisitedItemLevel[];
}

/**
 * Sets the last visited item at the specified level and clears items at higher levels.
 * 
 * @param {string} itemId - The ID of the item to set as the last visited.
 * @param {StorageKeyLastVisitedItemLevel} level - The level at which to set the last visited item.
 */
export function setLastVisitedItem(
  itemId: string | null,
  level: StorageKeyLastVisitedItemLevel,
) {
  const storageKeyLastVisitedItem = getStorageKeyLastVisitedItem(level);
  const lastVisitedItemId = getString(storageKeyLastVisitedItem);

  if (lastVisitedItemId !== itemId) {
    const lastVisitedItemsToBeCleared = getLevelsAfterCurrent(level);
    clearLastVisitedItems(...lastVisitedItemsToBeCleared);
  }

  itemId !== null
    ? setString(storageKeyLastVisitedItem, itemId)
    : setString(storageKeyLastVisitedItem);

  return;
}

/**
 * Gets the ID of the last visited item at the given level, if any.
 *
 * Reads session storage, which Vue cannot track. A list that highlights its last
 * visited row should read this **once** rather than call
 * `calculateBackgroundStyle` per row per render, and refresh it when the screen is
 * activated.
 *
 * @param {StorageKeyLastVisitedItemLevel} [level=StorageKeyLastVisitedItemLevel.LEVEL_1] - The level to read.
 * @returns {string | null} The last visited item ID, or null.
 */
export function getLastVisitedItem(
  level: StorageKeyLastVisitedItemLevel = StorageKeyLastVisitedItemLevel.LEVEL_1,
) {
  return getString(getStorageKeyLastVisitedItem(level));
}

/** The background style that marks the row the user last opened. */
export const LAST_VISITED_ITEM_BACKGROUND_STYLE = `background-color: ${COLOR_LAST_VISITED_ITEM}`;

/**
 * Gets the background style for an item, applying a specific style if it's the last visited item.
 *
 * @param {string} itemId - The ID of the item to check.
 * @param {StorageKeyLastVisitedItemLevel} [level=StorageKeyLastVisitedItemLevel.LEVEL_1] - The level to check for the last visited item.
 * @returns {string} The background style (if any) for the item.
 */
export function calculateBackgroundStyle(
  itemId: string,
  level: StorageKeyLastVisitedItemLevel = StorageKeyLastVisitedItemLevel.LEVEL_1,
) {
  return getLastVisitedItem(level) === itemId
    ? LAST_VISITED_ITEM_BACKGROUND_STYLE
    : "";
}

/**
 * Ensures that a given sentence ends with a full stop ('.').
 * If the sentence already ends with a full stop, it remains unchanged.
 *
 * @param {string} sentence - The input sentence to process.
 * @returns {string} The sentence with a full stop at the end.
 *
 * @example
 * addFullStop("Hello World"); // Returns: "Hello World."
 * addFullStop("Hello World."); // Returns: "Hello World."
 * addFullStop("  Hello World  "); // Returns: "Hello World."
 */
export function addFullStop(sentence: string): string {
  return sentence.trim().endsWith('.') ? sentence.trim() : `${sentence.trim()}.`;
}

export function createCustomTimestampForAllDocuments (date = new Date()): string {
  const yy = String(date.getFullYear()).slice(-2);
  const mm = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');

  return `${yy}${mm}${dd}${hh}${min}`;
}

export function calculateAssignedDeviceName(
  deviceTemplate: DeviceTemplate | null,
  deviceName: string,
) {
  if (!deviceTemplate) {
    return deviceName;
  }

  return `${deviceTemplate.deviceName} - ${deviceName}`;
}